import React, { useRef } from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';
import { Dropdown } from 'components/molecules/dropdown';
import { useUpdateHireMutation } from '../../../services/hires';
import { activeNetworkSelector } from '../../../redux/selectors';
import { trackEvent } from '../../../helpers/analytics';
import { Card } from '../../../components/atoms/Card';
import { teamInvolvementRendererOptions } from '../utils/teamInvolvementOptions';

const renderDescription = (description, descriptionBold) => {
  if (!description) return null;
  if (descriptionBold) {
    const parts = description.split(descriptionBold);
    return (
      <Text fontSize="14px" color="text.subtle" lineHeight="1.4" mt="4px">
        {parts[0]}
        <Text as="span" fontWeight="600">
          {descriptionBold}
        </Text>
        {parts[1]}
      </Text>
    );
  }
  return (
    <Text fontSize="14px" color="text.subtle" lineHeight="1.4" mt="4px">
      {description}
    </Text>
  );
};

export const HireTeamInvolvementRenderer = ({ data }) => {
  const { colors } = useTheme();
  const hire = data?.original;
  const activeNetwork = useSelector(activeNetworkSelector);
  const [updateHire] = useUpdateHireMutation();
  const dropdownRef = useRef(null);

  if (hire?.isSkeleton) {
    return (
      <ContentLoader
        speed={2}
        width={400}
        height={48}
        viewBox="0 0 400 48"
        backgroundColor={colors.neutral[50]}
        foregroundColor={colors.neutral[20]}
      >
        <rect x="8" y="16" rx="3" ry="3" width="80" height="12" />
      </ContentLoader>
    );
  }

  if (!hire) {
    return '';
  }

  const teamInvolvement = hire.teamInvolvement || hire.team_involvement || 'pending_review';
  const isPendingReview = teamInvolvement === 'pending_review';
  const currentValue = teamInvolvementRendererOptions.find((item) => item.value === teamInvolvement);

  const renderTrigger = () => {
    if (isPendingReview) {
      return (
        <Flex alignItems="center" sx={{ gap: '8px' }}>
          <Flex alignItems="center" sx={{ gap: '4px' }}>
            <Text fontSize="12px" fontWeight="500" color="text.main">
              Select
            </Text>
            <Box as={ChevronDown} size="16px" sx={{ color: 'neutral.400' }} />
          </Flex>
          <Box
            sx={{
              bg: 'purple.100', // #F4F0FF
              color: 'purple.400', // #6D33FF
              borderRadius: '99px',
              px: '8px',
              py: '4px',
              fontSize: '10px',
              fontWeight: '600',
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            To review
          </Box>
        </Flex>
      );
    }

    if (currentValue) {
      return (
        <Flex alignItems="center" sx={{ gap: '8px' }}>
          <Box
            sx={{
              bg: currentValue.badgeBg,
              color: currentValue.badgeColor,
              borderRadius: '99px',
              px: '8px',
              py: '4px',
              fontSize: '12px',
              fontWeight: '500',
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            {currentValue.badgeLabel}
          </Box>
          <Box as={ChevronDown} size="16px" sx={{ color: 'neutral.400' }} />
        </Flex>
      );
    }

    return (
      <Flex alignItems="center" sx={{ gap: '8px' }}>
        <Text fontSize="12px" fontWeight="500" color="text.main">
          Select
        </Text>
        <Box as={ChevronDown} size="16px" color="neutral.400" />
      </Flex>
    );
  };

  const onChangeTeamImpact = async (option) => {
    // Close dropdown immediately since we're doing optimistic updates
    if (dropdownRef.current) {
      dropdownRef.current.closeDropdown();
    }

    try {
      await updateHire({ hire, data: { teamInvolvement: option.value } }).unwrap();
      toast('Hire Report updated', { icon: 'success', id: 'hires' });
      trackEvent('hires:impact_change', {
        collection_id: activeNetwork.id,
        hire_id: hire.id,
        value: option.value,
      });
    } catch (error) {
      toast('Failed to update hire report', { icon: 'error', id: 'hires' });
    }
  };

  return (
    <Flex p="8px" alignItems="center" sx={{ height: '100%' }}>
      <Box flexGrow="1" minWidth="0">
        <Dropdown
          ref={dropdownRef}
          placement="bottom-start"
          trigger={
            <Flex alignItems="center" sx={{ gap: '4px', cursor: 'pointer' }}>
              {renderTrigger()}
            </Flex>
          }
        >
          <Card sx={{ width: '310px', p: 0, cursor: 'pointer' }} onClick={(event) => event.stopPropagation()}>
            {teamInvolvementRendererOptions.map((option) => (
              <Flex
                key={option.value}
                flexDirection="column"
                px="16px"
                py="8px"
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeTeamImpact(option);
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bg: 'neutral.20' },
                }}
              >
                <Text
                  fontSize="14px"
                  color={currentValue?.value === option.value ? 'text.dark' : 'text.main'}
                  lineHeight="1.4"
                >
                  {option.label}
                </Text>
                {renderDescription(option.description, option.descriptionBold)}
              </Flex>
            ))}
          </Card>
        </Dropdown>
      </Box>
    </Flex>
  );
};

HireTeamInvolvementRenderer.propTypes = {
  data: PropTypes.object,
};

HireTeamInvolvementRenderer.defaultProps = {
  data: {},
};
